// ============================================================
// interpolation.ts — Remote entity interpolation with jitter
// buffer and adaptive delay
// ============================================================

import { PlayerSnapshot, WorldSnapshot } from './messages';
import { lerp, lerpAngle } from '../utils/math';

/** Number of snapshots to keep in the buffer per entity. */
const MAX_BUFFER_SIZE = 30;

/** Default interpolation delay in milliseconds. Roughly 2 ticks at 64 tick (31.25ms). */
const DEFAULT_INTERP_DELAY_MS = 100;

/** Jitter buffer target multiplier: adaptive delay = measuredJitter * this. */
const JITTER_BUFFER_MULTIPLIER = 1.5;

/** Number of recent arrivals to track for jitter measurement. */
const JITTER_WINDOW_SIZE = 30;

/** Minimum adaptive delay (ms). */
const MIN_ADAPTIVE_DELAY_MS = 20;

/** Maximum adaptive delay (ms). */
const MAX_ADAPTIVE_DELAY_MS = 200;

// --- Single-entity interpolation buffer ---

interface TimedSnapshot {
  time: number; // server time in ms
  state: PlayerSnapshot;
}

export class InterpolationBuffer {
  private buffer: TimedSnapshot[] = [];
  private interpDelay: number;

  constructor(interpDelayMs: number = DEFAULT_INTERP_DELAY_MS) {
    this.interpDelay = interpDelayMs;
  }

  push(time: number, state: PlayerSnapshot): void {
    this.buffer.push({ time, state });

    // Keep buffer bounded
    if (this.buffer.length > MAX_BUFFER_SIZE) {
      this.buffer.shift();
    }
  }

  /**
   * Sample the interpolated state at `renderTime`.
   * renderTime should be serverTime - interpolationDelay.
   * Returns null if not enough data to interpolate.
   */
  sample(renderTime: number): PlayerSnapshot | null {
    const buf = this.buffer;
    if (buf.length < 2) return buf.length === 1 ? { ...buf[0].state } : null;

    // Find the two surrounding snapshots
    let from: TimedSnapshot | null = null;
    let to: TimedSnapshot | null = null;

    for (let i = 0; i < buf.length - 1; i++) {
      if (buf[i].time <= renderTime && buf[i + 1].time >= renderTime) {
        from = buf[i];
        to = buf[i + 1];
        break;
      }
    }

    // If renderTime is before all snapshots, use earliest
    if (from === null && renderTime < buf[0].time) {
      return { ...buf[0].state };
    }

    // If renderTime is after all snapshots, use latest (extrapolation not desired)
    if (from === null) {
      return { ...buf[buf.length - 1].state };
    }

    // Interpolation factor
    const range = to!.time - from.time;
    const t = range > 0 ? (renderTime - from.time) / range : 0;

    return interpolateSnapshots(from.state, to!.state, t);
  }

  /** Remove snapshots older than a cutoff to prevent unbounded growth. */
  prune(oldestTime: number): void {
    // Keep at least 2 entries for interpolation
    while (this.buffer.length > 2 && this.buffer[0].time < oldestTime) {
      this.buffer.shift();
    }
  }

  getDelay(): number {
    return this.interpDelay;
  }

  setDelay(delayMs: number): void {
    this.interpDelay = delayMs;
  }
}

// --- Interpolate between two snapshots ---

function interpolateSnapshots(a: PlayerSnapshot, b: PlayerSnapshot, t: number): PlayerSnapshot {
  // CN-5: spread newer snapshot as base so all fields (isGrounded, isSliding, skinId,
  // money, team, grenades, reloading, inspecting, etc.) carry through. Then overwrite
  // interpolated / discrete fields.
  return {
    ...b,
    id: b.id,
    position: {
      x: lerp(a.position.x, b.position.x, t),
      y: lerp(a.position.y, b.position.y, t),
      z: lerp(a.position.z, b.position.z, t),
    },
    yaw: lerpAngle(a.yaw, b.yaw, t),
    pitch: lerp(a.pitch, b.pitch, t),
    health: b.health,       // discrete — use latest
    armor: b.armor,         // discrete — use latest
    weapon: b.weapon,       // discrete — use latest
    isCrouching: b.isCrouching, // discrete — use latest
  };
}

// ============================================================
// Jitter Buffer — measures snapshot arrival variance
// ============================================================

export class JitterBuffer {
  /** Recent inter-arrival times in ms. */
  private arrivalDeltas: number[] = [];
  private lastArrivalTime = 0;
  private _measuredJitter = 0;
  private _adaptiveDelay = DEFAULT_INTERP_DELAY_MS;

  /** Record a snapshot arrival. Call every time a new snapshot is received. */
  recordArrival(): void {
    const now = performance.now();
    if (this.lastArrivalTime > 0) {
      const delta = now - this.lastArrivalTime;
      this.arrivalDeltas.push(delta);

      // Keep window bounded
      if (this.arrivalDeltas.length > JITTER_WINDOW_SIZE) {
        this.arrivalDeltas.shift();
      }

      this.recalculate();
    }
    this.lastArrivalTime = now;
  }

  private recalculate(): void {
    if (this.arrivalDeltas.length < 3) return;

    // Calculate mean inter-arrival time
    let sum = 0;
    for (const d of this.arrivalDeltas) sum += d;
    const mean = sum / this.arrivalDeltas.length;

    // Calculate standard deviation (jitter)
    let varianceSum = 0;
    for (const d of this.arrivalDeltas) {
      const diff = d - mean;
      varianceSum += diff * diff;
    }
    this._measuredJitter = Math.sqrt(varianceSum / this.arrivalDeltas.length);

    // Adaptive delay = jitter * multiplier, clamped
    const target = this._measuredJitter * JITTER_BUFFER_MULTIPLIER;
    this._adaptiveDelay = Math.max(MIN_ADAPTIVE_DELAY_MS, Math.min(MAX_ADAPTIVE_DELAY_MS, target));

    // Ensure adaptive delay is at least as large as the mean inter-arrival time
    // to always have a snapshot pair available
    this._adaptiveDelay = Math.max(this._adaptiveDelay, mean * 1.2);
    this._adaptiveDelay = Math.min(this._adaptiveDelay, MAX_ADAPTIVE_DELAY_MS);
  }

  /** Get measured jitter (standard deviation of inter-arrival times) in ms. */
  getMeasuredJitter(): number {
    return this._measuredJitter;
  }

  /** Get the adaptive interpolation delay in ms. */
  getAdaptiveDelay(): number {
    return this._adaptiveDelay;
  }

  reset(): void {
    this.arrivalDeltas = [];
    this.lastArrivalTime = 0;
    this._measuredJitter = 0;
    this._adaptiveDelay = DEFAULT_INTERP_DELAY_MS;
  }
}

// ============================================================
// Smooth Correction — exponential decay blend over 100ms
// ============================================================

/** Duration to blend correction over (ms). */
const CORRECTION_BLEND_MS = 100;

export class SmoothCorrection {
  private offsetX = 0;
  private offsetY = 0;
  private offsetZ = 0;
  private blendStartTime = 0;
  private blendDuration = CORRECTION_BLEND_MS;

  /**
   * Apply a correction: instead of snapping, store offset and blend it out.
   * correctedPos = the position after server reconciliation.
   * currentPos = the position we're currently rendering.
   */
  addCorrection(currentX: number, currentY: number, currentZ: number,
                correctedX: number, correctedY: number, correctedZ: number): void {
    // Accumulate new error into existing offset
    this.offsetX += currentX - correctedX;
    this.offsetY += currentY - correctedY;
    this.offsetZ += currentZ - correctedZ;
    this.blendStartTime = performance.now();
  }

  /**
   * Update the correction offset each frame. Uses exponential decay.
   * Returns the visual offset to add to the player's rendered position.
   */
  update(dtMs: number): { x: number; y: number; z: number } {
    if (this.offsetX === 0 && this.offsetY === 0 && this.offsetZ === 0) {
      return { x: 0, y: 0, z: 0 };
    }

    // Exponential decay: factor = e^(-dt / halflife)
    // We want ~95% blended in CORRECTION_BLEND_MS, so halflife ~ CORRECTION_BLEND_MS / 3
    const halflife = this.blendDuration / 3;
    const factor = Math.exp(-dtMs / halflife);

    this.offsetX *= factor;
    this.offsetY *= factor;
    this.offsetZ *= factor;

    // Zero out if negligible
    const magSq = this.offsetX * this.offsetX +
                  this.offsetY * this.offsetY +
                  this.offsetZ * this.offsetZ;
    if (magSq < 0.0001) {
      this.offsetX = 0;
      this.offsetY = 0;
      this.offsetZ = 0;
    }

    return { x: this.offsetX, y: this.offsetY, z: this.offsetZ };
  }

  reset(): void {
    this.offsetX = 0;
    this.offsetY = 0;
    this.offsetZ = 0;
  }
}

// ============================================================
// Prediction Error History — for debug visualization
// ============================================================

const ERROR_HISTORY_SIZE = 120; // ~2 seconds at 60fps

export class PredictionErrorHistory {
  private errors: number[] = [];

  record(errorMagnitude: number): void {
    this.errors.push(errorMagnitude);
    if (this.errors.length > ERROR_HISTORY_SIZE) {
      this.errors.shift();
    }
  }

  /** Get the error history array (most recent last). */
  getHistory(): readonly number[] {
    return this.errors;
  }

  /** Get average prediction error. */
  getAverage(): number {
    if (this.errors.length === 0) return 0;
    let sum = 0;
    for (const e of this.errors) sum += e;
    return sum / this.errors.length;
  }

  /** Get max prediction error in the window. */
  getMax(): number {
    if (this.errors.length === 0) return 0;
    let max = 0;
    for (const e of this.errors) {
      if (e > max) max = e;
    }
    return max;
  }

  reset(): void {
    this.errors = [];
  }
}

// --- Multi-entity interpolator ---

export class EntityInterpolator {
  private buffers = new Map<number, InterpolationBuffer>();
  private interpDelayMs: number;
  private jitterBuffer = new JitterBuffer();
  private smoothCorrection = new SmoothCorrection();
  private predictionErrorHistory = new PredictionErrorHistory();
  private useAdaptiveDelay = true;

  constructor(interpDelayMs: number = DEFAULT_INTERP_DELAY_MS) {
    this.interpDelayMs = interpDelayMs;
  }

  /**
   * Feed a world snapshot into the interpolator.
   * `serverTime` is the server timestamp (ms) associated with this snapshot's tick.
   */
  addSnapshot(snapshot: WorldSnapshot, serverTime: number): void {
    // Record arrival for jitter measurement
    this.jitterBuffer.recordArrival();

    // Update adaptive delay on each individual buffer
    if (this.useAdaptiveDelay) {
      const adaptiveDelay = this.jitterBuffer.getAdaptiveDelay();
      for (const buf of this.buffers.values()) {
        buf.setDelay(adaptiveDelay);
      }
    }

    for (const player of snapshot.players) {
      let buf = this.buffers.get(player.id);
      if (!buf) {
        const delay = this.useAdaptiveDelay
          ? this.jitterBuffer.getAdaptiveDelay()
          : this.interpDelayMs;
        buf = new InterpolationBuffer(delay);
        this.buffers.set(player.id, buf);
      }
      buf.push(serverTime, player);
    }
  }

  /**
   * Get the interpolated state for a remote player.
   * `renderTime` should be currentServerTime - interpolationDelay.
   */
  getInterpolatedState(playerId: number, renderTime: number): PlayerSnapshot | null {
    const buf = this.buffers.get(playerId);
    if (!buf) return null;
    return buf.sample(renderTime);
  }

  removePlayer(id: number): void {
    this.buffers.delete(id);
  }

  /** Prune old data from all buffers. */
  pruneAll(oldestTime: number): void {
    for (const buf of this.buffers.values()) {
      buf.prune(oldestTime);
    }
  }

  /** Get the current interpolation delay in ms (adaptive or fixed). */
  getInterpolationDelay(): number {
    if (this.useAdaptiveDelay) {
      return this.jitterBuffer.getAdaptiveDelay();
    }
    return this.interpDelayMs;
  }

  /** Get count of tracked entities. */
  getTrackedCount(): number {
    return this.buffers.size;
  }

  /** Get the jitter buffer for debug info. */
  getJitterBuffer(): JitterBuffer {
    return this.jitterBuffer;
  }

  /** Get the smooth correction system. */
  getSmoothCorrection(): SmoothCorrection {
    return this.smoothCorrection;
  }

  /** Get prediction error history for debug visualization. */
  getPredictionErrorHistory(): PredictionErrorHistory {
    return this.predictionErrorHistory;
  }

  /** Record a prediction error magnitude (called after reconciliation). */
  recordPredictionError(error: number): void {
    this.predictionErrorHistory.record(error);
  }

  /** Enable or disable adaptive jitter-based delay. */
  setAdaptiveDelay(enabled: boolean): void {
    this.useAdaptiveDelay = enabled;
  }

  /** Reset jitter buffer and correction state (e.g. on round start). */
  resetState(): void {
    this.jitterBuffer.reset();
    this.smoothCorrection.reset();
    this.predictionErrorHistory.reset();
  }
}
